/**
 * Gestión de caché en sessionStorage con validación por día
 * 
 * Este módulo proporciona funciones para:
 * - Envolver datos con metadata (fecha y timestamp)
 * - Validar que la caché sea del día actual
 * - Descartar automáticamente cachés expiradas
 */

/**
 * Obtiene la fecha actual en formato YYYY-MM-DD
 * @returns String con la fecha actual
 */
export const getCurrentDateString = (): string =>
{
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Valida si una fecha de caché corresponde al día actual
 * @param cachedDate - Fecha en formato YYYY-MM-DD a validar
 * @returns true si la caché es válida (mismo día), false en caso contrario
 */
export const isCacheValid = (cachedDate: string): boolean =>
{
    const currentDate = getCurrentDateString();
    const isValid = cachedDate === currentDate;
    
    return isValid;
};

/**
 * Envuelve datos con metadata (fecha y timestamp) para almacenamiento
 * @param data - Datos a envolver
 * @returns String JSON con los datos y metadata
 */
export const wrapWithMetadata = <T,>(data: T): string =>
{
    const metadata =
    {
        data: data,
        cachedDate: getCurrentDateString(),
        timestamp: Date.now()
    };
    return JSON.stringify(metadata);
};

/**
 * Desenvuelve y valida datos almacenados con metadata
 * Descarta automáticamente cachés expiradas o sin metadata
 * @param jsonString - String JSON a parsear
 * @returns Los datos si son válidos, null si están expirados o son inválidos
 */
export const unwrapMetadata = <T,>(jsonString: string): T | null =>
{
    try
    {
        const parsed = JSON.parse(jsonString);
        
        // Verificar si tiene el formato con metadata
        if(parsed.cachedDate && parsed.data)
        {
            // Validar que la caché sea del día actual
            if(!isCacheValid(parsed.cachedDate))
            {
                return null;
            }
            return parsed.data as T;
        }
        
        // Si no tiene metadata, es caché antigua - la descartamos
        return null;
    }
    catch(error)
    {
        console.error('❌ [unwrapMetadata] Error al parsear datos:', error);
        return null;
    }
};